import React, { useCallback, useEffect, useState } from 'react';
import apiService from '../services/ApiService';
import PageHeader, { RefreshButton } from '../components/shell/PageHeader';
import { useToast } from '../contexts/ToastContext';

interface JobRow {
    id: string;
    name: string;
    state: string;
    createdAtUtc: string;
    startedAtUtc?: string;
    finishedAtUtc?: string;
    ownerId?: number;
    error?: string;
    scheduleId?: string;
}
interface ScheduleRow { id: string; cron: string; lastQueuedAtUtc?: string }
interface QueueState { paused: boolean; items: JobRow[]; schedules: ScheduleRow[] }

const JobsPage: React.FC = () => {
    const { showToast } = useToast();
    const [data, setData] = useState<QueueState>({ paused: false, items: [], schedules: [] });
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const load = useCallback(async () => {
        try { setData(await apiService.get<QueueState>('/api/admin/jobs')); }
        catch { showToast('Errore', 'Impossibile caricare la coda', 'danger'); }
        finally { setLoading(false); }
    }, [showToast]);

    useEffect(() => { void load(); }, [load]);

    const act = async (path: string) => {
        setBusy(true);
        try { await apiService.post(`/api/admin/jobs/${path}`); await load(); }
        catch { showToast('Errore', 'Operazione non riuscita', 'danger'); }
        finally { setBusy(false); }
    };

    return <>
        <PageHeader
            title="Code e job"
            description="Job dell’istanza API corrente. Lo stato non sopravvive a un riavvio."
            actions={<>
                <RefreshButton onClick={() => void load()} loading={loading || busy} />
                <button className="btn btn-outline-secondary btn-sm" onClick={() => void act(data.paused ? 'resume' : 'pause')} disabled={busy}>
                    <i className={`bi ${data.paused ? 'bi-play-fill' : 'bi-pause-fill'} me-1`} />{data.paused ? 'Riprendi coda' : 'Sospendi coda'}
                </button>
            </>}
        />
        <div className="card shadow-sm mb-3"><div className="card-body">
            <strong>Stato:</strong> {data.paused ? 'sospesa' : 'attiva'} · {data.items.filter(j => j.state === 'Queued').length} in attesa · {data.items.filter(j => j.state === 'Running').length} in esecuzione
        </div></div>
        <div className="card shadow-sm mb-3"><div className="card-header">Job recenti</div><div className="table-responsive"><table className="table table-sm align-middle mb-0">
            <thead><tr><th>Creato</th><th>Job</th><th>Stato</th><th>Origine</th><th>Errore</th><th>Azioni</th></tr></thead>
            <tbody>{data.items.length === 0 && <tr><td colSpan={6} className="text-center text-body-secondary p-4">Nessun job.</td></tr>}
                {data.items.map(job => <tr key={job.id}>
                    <td className="small text-nowrap">{new Date(job.createdAtUtc).toLocaleString('it-IT')}</td>
                    <td><div>{job.name}</div><code className="small">{job.id.slice(0, 12)}</code></td>
                    <td><span className={`badge ${job.state === 'Succeeded' ? 'bg-success' : job.state === 'Failed' ? 'bg-danger' : 'bg-secondary'}`}>{job.state}</span></td>
                    <td className="small">{job.scheduleId || (job.ownerId ? `Utente #${job.ownerId}` : 'Manuale')}</td>
                    <td className="small text-danger">{job.error || '—'}</td>
                    <td className="text-nowrap">{job.state === 'Queued' && <button className="btn btn-outline-danger btn-sm" disabled={busy} onClick={() => void act(`${job.id}/cancel`)}>Annulla</button>}
                        {(job.state === 'Failed' || job.state === 'Cancelled') && <button className="btn btn-outline-primary btn-sm" disabled={busy} onClick={() => void act(`${job.id}/retry`)}>Riprova</button>}</td>
                </tr>)}</tbody>
        </table></div></div>
        <div className="card shadow-sm"><div className="card-header">Pianificazioni attive</div><div className="table-responsive"><table className="table table-sm mb-0">
            <thead><tr><th>ID</th><th>Cron UTC</th><th>Ultimo accodamento</th></tr></thead>
            <tbody>{data.schedules.length === 0 && <tr><td colSpan={3} className="text-center text-body-secondary p-4">Nessuna pianificazione.</td></tr>}
                {data.schedules.map(schedule => <tr key={schedule.id}><td>{schedule.id}</td><td><code>{schedule.cron}</code></td><td>{schedule.lastQueuedAtUtc ? new Date(schedule.lastQueuedAtUtc).toLocaleString('it-IT') : '—'}</td></tr>)}</tbody>
        </table></div></div>
    </>;
};

export default JobsPage;
